import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router';
import { CoreProvider } from '@lista/app-core';
import { queryKeys, services, setSessionExpiredHandler, usersService } from '../shared/api';
import { newId } from '../shared/lib/hooks';
import { useSession } from '../shared/auth/session-store';
import { applyTheme, storedTheme } from '../shared/lib/theme';
import { clearApiCaches } from '../shared/pwa/cache';
import { ConsentBanner } from '../shared/privacy/ConsentBanner';
import { UpdatePrompt } from '../shared/pwa/UpdatePrompt';
import { ConfirmHost, toast, Toaster } from '../shared/ui';
import { createQueryClient } from './query-client';
import { router } from './router';

applyTheme(storedTheme());

export function App() {
  const [queryClient] = useState(createQueryClient);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (useSession.getState().status !== 'authenticated') return;
      useSession.getState().signOut();
      queryClient.clear();
      void clearApiCaches();
      toast.info(t('auth.sessionExpired'));
    });
  }, [queryClient, t]);

  return (
    <QueryClientProvider client={queryClient}>
      <CoreProvider value={{ services, locale: i18n.language, newId }}>
        <ProfilePreferences />
        <RouterProvider router={router} />
        <Toaster />
        <ConfirmHost />
        <UpdatePrompt />
        <ConsentBanner />
      </CoreProvider>
    </QueryClientProvider>
  );
}

/** Applies the signed-in user's saved theme. */
function ProfilePreferences() {
  const signedIn = useSession((s) => s.status === 'authenticated');
  const profile = useQuery({ queryKey: queryKeys.me, queryFn: usersService.me, enabled: signedIn });
  const theme = profile.data?.theme;
  useEffect(() => {
    if (theme) applyTheme(theme);
  }, [theme]);
  return null;
}
